import {
    BalanceChanges,
    fetchAndDeserializeVersionedTransaction,
    fetchAndDeserializeVersionedTransactionsWithLoanInfo,
    resolveAddressLookups,
    simulateTransactionAndGetBalanceChanges,
    TransactionsWithDataGenerateReturn,
    deserializeVersionedTransaction,
    AnalyticsInfo
} from "@bridgesplit/utils";
import { MessageAccountKeys, PublicKey } from "@solana/web3.js";

import {
    AbfTransactionHeaders,
    TXN_CREDITBOOK_REPAY,
    RepayLoanTxnParams,
    TXN_CREDITBOOK_DEPOSIT_COLLATERAL,
    DepositCollateralParams,
    TXN_CREDITBOOK_WITHDRAW_COLLATERAL,
    WithdrawCollateralTxnParams,
    TXN_CREDITBOOK_REFINANCE,
    RefinanceLoanTxnParams,
    TXN_CREDITBOOK_MERGE_LOAN,
    MergeLoanTxnParams,
    SwapCollateralTxnParams,
    TXN_CREDITBOOK_SWAP_COLLATERAL,
    BuildLoanTransactionsRequest,
    BuildLoanTransactionsResponse,
    TXN_CREDITBOOK_BUILD_LOAN,
    SdkApiLoanInfo
} from "../types";
import { createMarketsInstance } from "./auth";

export async function getRepayLoanTransactionAndBalanceChanges(
    authentication: AbfTransactionHeaders,
    args: RepayLoanTxnParams,
    queryParams?: object,
    analyticsInfo?: AnalyticsInfo
): Promise<TransactionsWithDataGenerateReturn<{ balanceChanges: BalanceChanges }>> {
    const { transactions } = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        RepayLoanTxnParams,
        SdkApiLoanInfo
    >(createMarketsInstance(authentication), TXN_CREDITBOOK_REPAY, args);

    if (!args.simulate) {
        return { transactions, balanceChanges: {} };
    }

    // Bundled flows (e.g., oracle crank prepended) cannot be simulated standalone —
    // the user tx depends on state that only lands after the preceding tx.
    if (transactions.length > 1) {
        return { transactions, balanceChanges: {} };
    }

    // Use the final transaction as the best proxy for the repay output
    const transaction = transactions[transactions.length - 1];

    const accountsToTrack: PublicKey[] = [];
    const user = authentication.userWallet ? new PublicKey(authentication.userWallet) : PublicKey.default;
    const lutAccounts = await resolveAddressLookups(transaction);
    const accountKeys: MessageAccountKeys = transaction.message.getAccountKeys({
        addressLookupTableAccounts: lutAccounts
    });
    const staticAccountsToTrack: PublicKey[] = accountKeys.staticAccountKeys.filter(
        (_, idx) => transaction.message.isAccountWritable(idx) || transaction.message.isAccountSigner(idx)
    );

    if (staticAccountsToTrack.length > 0) {
        accountsToTrack.push(...staticAccountsToTrack);
    }

    if (accountKeys.accountKeysFromLookups) {
        accountsToTrack.push(...accountKeys.accountKeysFromLookups.writable);
    }

    const { balanceChanges } = await simulateTransactionAndGetBalanceChanges({
        transaction,
        user,
        accountsToTrack: accountsToTrack,
        analyticsInfo: {
            ...analyticsInfo,
            loanAddress: args.loan
        }
    });

    return { transactions, balanceChanges };
}

export async function getDepositCollateralTransaction(
    authentication: AbfTransactionHeaders,
    args: DepositCollateralParams
) {
    return [
        await fetchAndDeserializeVersionedTransaction(
            createMarketsInstance(authentication),
            TXN_CREDITBOOK_DEPOSIT_COLLATERAL,
            args
        )
    ];
}

export async function getSwapCollateralTransaction(
    authentication: AbfTransactionHeaders,
    args: SwapCollateralTxnParams
) {
    const { transactions } = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        SwapCollateralTxnParams,
        SdkApiLoanInfo
    >(createMarketsInstance(authentication), TXN_CREDITBOOK_SWAP_COLLATERAL, args);
    return transactions;
}

export async function getWithdrawCollateralTransaction(
    authentication: AbfTransactionHeaders,
    args: WithdrawCollateralTxnParams
) {
    return [
        await fetchAndDeserializeVersionedTransaction(
            createMarketsInstance(authentication),
            TXN_CREDITBOOK_WITHDRAW_COLLATERAL,
            args
        )
    ];
}

export async function getRefinanceLoanTransaction(authentication: AbfTransactionHeaders, args: RefinanceLoanTxnParams) {
    const { transactions } = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        RefinanceLoanTxnParams,
        SdkApiLoanInfo
    >(createMarketsInstance(authentication), TXN_CREDITBOOK_REFINANCE, args);
    return transactions;
}

export async function getMergeLoanTransaction(authentication: AbfTransactionHeaders, args: MergeLoanTxnParams) {
    const { transactions } = await fetchAndDeserializeVersionedTransactionsWithLoanInfo<
        MergeLoanTxnParams,
        SdkApiLoanInfo
    >(createMarketsInstance(authentication), TXN_CREDITBOOK_MERGE_LOAN, args);
    return transactions;
}

export async function buildLoanTransactions(
    authentication: AbfTransactionHeaders,
    args: BuildLoanTransactionsRequest
): Promise<TransactionsWithDataGenerateReturn<{ loanAddress: string }>> {
    const api = createMarketsInstance(authentication);
    const response = await api.post<BuildLoanTransactionsResponse>(TXN_CREDITBOOK_BUILD_LOAN, args);

    return {
        transactions: response.data.transactions.map((transaction) => deserializeVersionedTransaction(transaction)),
        loanAddress: response.data.loanAddress
    };
}
